import React, { useState, useEffect, useReducer, useContext, useMemo, useRef, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { FixedSizeList } from "react-window";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
   
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

const TICKET_ROW_HEIGHT = 92;

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;
        const nextState = [...state];

        newTickets.forEach((ticket) => {
            const ticketIndex = nextState.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                nextState[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    nextState.unshift(nextState.splice(ticketIndex, 1)[0]);
                }
            } else {
                nextState.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;
        const nextState = state.map((ticket) =>
            ticket.id === ticketId ? { ...ticket, unreadMessages: 0 } : ticket
        );

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;
        const nextState = [...state];

        const ticketIndex = nextState.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            nextState[ticketIndex] = ticket;
        } else {
            nextState.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;
        const nextState = [...state];

        const ticketIndex = nextState.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            nextState[ticketIndex] = ticket;
            nextState.unshift(nextState.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                nextState.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        return state.map((ticket) =>
            ticket.contactId === contact.id ? { ...ticket, contact } : ticket
        );
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const nextState = [...state];
        const ticketIndex = nextState.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            nextState.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? nextState.sort(ticketSortAsc) : nextState.sort(ticketSortDesc);
        }

        return nextState;
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const listContainerRef = useRef(null);
    const [listHeight, setListHeight] = useState(0);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        skipCount: "true"
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    const statusMatches = (ticketStatus) => {
        if (!status || status === "search") return true;
        if (status === "closed") return ["closed", "nps"].includes(ticketStatus);
        return ticketStatus === status;
    };

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1)
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && statusMatches(data.ticket.status)) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // else if (data.action === "update" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET",
            //         payload: data.ticket,
            //     });
            // }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && statusMatches(data.ticket.status)) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        onConnectTicketsList();

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    useEffect(() => {
        const updateListHeight = () => {
            if (listContainerRef.current) {
                setListHeight(listContainerRef.current.clientHeight);
            }
        };

        updateListHeight();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateListHeight);
            return () => window.removeEventListener("resize", updateListHeight);
        }

        const resizeObserver = new ResizeObserver(updateListHeight);
        if (listContainerRef.current) {
            resizeObserver.observe(listContainerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const loadMore = useCallback(() => {
        setPageNumber((prevState) => prevState + 1);
    }, []);

    const handleItemsRendered = useCallback(({ visibleStopIndex }) => {
        if (!hasMore || loading) return;

        if (visibleStopIndex >= ticketsList.length - 5) {
            loadMore();
        }
    }, [hasMore, loading, ticketsList.length, loadMore]);

    const itemData = useMemo(() => ({
        tickets: ticketsList,
        setTabOpen
    }), [ticketsList, setTabOpen]);

    const renderTicketRow = useCallback(({ index, style: rowStyle, data }) => {
        const ticket = data.tickets[index];

        return (
            <TicketListItem
                ticket={ticket}
                key={ticket.id}
                setTabOpen={data.setTabOpen}
                style={rowStyle}
            />
        );
    }, []);

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => statusMatches(ticket.status));
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                ref={listContainerRef}
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
            >
                {ticketsList.length === 0 && !loading ? (
                    <div className={classes.noTicketsDiv}>
                        <span className={classes.noTicketsTitle}>
                            {i18n.t("ticketsList.noTicketsTitle")}
                        </span>
                        <p className={classes.noTicketsText}>
                            {i18n.t("ticketsList.noTicketsMessage")}
                        </p>
                    </div>
                ) : listHeight > 0 ? (
                    <FixedSizeList
                        height={listHeight}
                        width="100%"
                        itemCount={ticketsList.length}
                        itemSize={TICKET_ROW_HEIGHT}
                        itemData={itemData}
                        itemKey={(index, data) => data.tickets[index]?.id || index}
                        onItemsRendered={handleItemsRendered}
                        overscanCount={5}
                    >
                        {renderTicketRow}
                    </FixedSizeList>
                ) : null}
                {loading && <TicketsListSkeleton />}
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
